/**
 * render.ts — HTML5 canvas renderer.
 *
 * Layers, drawn back-to-front each frame:
 *   1. Floor           — flat dark grid background.
 *   2. Belief heat-map  — bGold (warm) and bStone (cool), drawn only on cells
 *                         the agent has sensed (known != UNKNOWN), so the
 *                         viewer can SEE the agent's knowledge grow.
 *   3. World truth      — painted gold (gold) and stones (slate). Only
 *                         shown on cells the agent has visited or sensed
 *                         (so the demo doesn't leak the hidden state to the
 *                         viewer before the agent has found it).
 *   4. Visited overlay  — a faint tint on cells the agent has stood on.
 *   5. Frontier marker  — a ring on the planner's current goal.
 *   6. Agent             — a disc with a heading tick, eased between cells.
 *
 * The renderer is imperative: a rAF loop + direct canvas writes. State lives
 * in `World` (the ground truth), `Env` (the run state + belief), and the
 * agent animation state held here. The renderer reads, never mutates.
 *
 * Painting is handled in main.ts (it needs the DOM event → cell mapping);
 * the renderer only draws what it's given.
 */

import { World } from './environment';
import { Env } from './environment';
import { UNKNOWN, FREE, GOLD, STONE, SENSOR_RADIUS } from './belief';

const FLOOR = '#0b1a24';
const GRID_LINE = 'rgba(255,255,255,0.04)';
const GOLD_COLOR = '#e0b34a';
const GOLD_HIGHLIGHT = '#f7dd92';
const GOLD_SHADOW = '#7a5512';
const GOLD_INNER = '#c08f1a';
const STONE_COLOR = '#4a5b6b';
const STONE_HIGHLIGHT = '#7c93a6';
const STONE_SHADOW = '#28323e';
const STONE_CRACK = '#1a232e';
/** Alpha for painted cells the agent has NOT sensed yet. Visible enough to
 *  read what you drew, dim enough that "revealed" cells pop as it explores. */
const GOLD_FADE = 0.28;
const STONE_FADE = 0.22;
const VISITED_TINT = 'rgba(120, 200, 255, 0.05)';
const AGENT_BODY = '#cde8ff';
const AGENT_RING = '#4c72b0';
const SENSOR_RING = 'rgba(205, 232, 255, 0.18)';
const FRONTIER_RING = '#55a868';

export interface AgentAnim {
  /** Current pose in cell coords (integer). */
  r: number;
  c: number;
  /** Smoothed pixel position (cells), eased toward (r, c). */
  sr: number;
  sc: number;
  /** Heading in radians, eased toward the last move direction. */
  heading: number;
  targetHeading: number;
  /** Set when the agent bumps; counts down to 0 over a few frames. */
  bumpFlash: number;
}

export function makeAgentAnim(r: number, c: number): AgentAnim {
  return { r, c, sr: r, sc: c, heading: 0, targetHeading: 0, bumpFlash: 0 };
}

export interface RenderState {
  world: World;
  env: Env;
  agent: AgentAnim;
  /** The frontier planner's current goal (or null). */
  goal: { r: number; c: number } | null;
  /** True if "show full ground truth" is toggled on (debug / reveal). */
  revealAll: boolean;
  /** True if the belief heat-map should be drawn. */
  showBelief: boolean;
  /** True if the mixture-of-bandits gate is firing this frame — draws a
   *  green halo on the agent (mirrors the bump-flash pattern). */
  gateFiring: boolean;
  /** True if the queen meta-action fired this frame — draws a blue halo
   *  on the agent (slightly larger than the gate's green halo; the two
   *  never co-occur since queen actions live at indices the gate doesn't
   *  mask). */
  queenFiring: boolean;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private bg: HTMLCanvasElement;
  beliefDirty = true;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas not supported');
    this.ctx = ctx;
    this.bg = document.createElement('canvas');
    this.resize();
  }

  /** Call on viewport resize; re-renders the static floor. */
  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (w === 0 || h === 0) return;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.renderBackground();
  }

  get widthPx(): number {
    return this.canvas.getBoundingClientRect().width;
  }
  get heightPx(): number {
    return this.canvas.getBoundingClientRect().height;
  }

  /** Pixel size of one cell at the current canvas size. */
  cellPx(state: RenderState): number {
    const g = state.world.grid;
    const w = this.widthPx;
    const h = this.heightPx;
    return Math.min(w / g, h / g);
  }

  /** Pre-render the static floor + grid lines. */
  private renderBackground(): void {
    const w = this.widthPx;
    const h = this.heightPx;
    this.bg.width = Math.floor(w);
    this.bg.height = Math.floor(h);
    const ctx = this.bg.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = FLOOR;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = GRID_LINE;
    ctx.lineWidth = 1;
    // Grid lines are drawn per-frame against the live grid size, since the
    // grid can change between runs. Here we just lay the floor.
  }

  /** One frame. `t` is a monotonic clock in ms. */
  frame(t: number, state: RenderState): void {
    const w = this.widthPx;
    const h = this.heightPx;
    if (w === 0 || h === 0 || this.bg.width === 0 || this.bg.height === 0) {
      this.resize();
      if (this.bg.width === 0 || this.bg.height === 0) return;
    }
    const ctx = this.ctx;
    const g = state.world.grid;
    const cell = this.cellPx(state);
    const ox = (w - cell * g) / 2; // origin offset to centre the grid
    const oy = (h - cell * g) / 2;

    // Layer 1 — floor.
    ctx.drawImage(this.bg, 0, 0, w, h);

    // Grid lines.
    ctx.strokeStyle = GRID_LINE;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= g; i++) {
      ctx.moveTo(ox + i * cell, oy);
      ctx.lineTo(ox + i * cell, oy + g * cell);
      ctx.moveTo(ox, oy + i * cell);
      ctx.lineTo(ox + g * cell, oy + i * cell);
    }
    ctx.stroke();

    // Layer 2 — belief heat-map (gold warm, stone cool), only on known cells.
    if (state.showBelief) {
      this.drawBelief(state, cell, ox, oy);
    }

    // Layer 3 — ground truth (gold + stones), only on sensed/visited cells
    // unless revealAll is on.
    this.drawWorld(state, cell, ox, oy);

    // Layer 4 — visited tint.
    this.drawVisited(state, cell, ox, oy);

    // Layer 5 — frontier goal marker.
    if (state.goal) {
      const gx = ox + (state.goal.c + 0.5) * cell;
      const gy = oy + (state.goal.r + 0.5) * cell;
      ctx.strokeStyle = FRONTIER_RING;
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(gx, gy, cell * 0.4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Layer 6 — agent (eased).
    this.drawAgent(state, cell, ox, oy, t);
  }

  /** Belief heat-map: each known cell tinted by bGold (warm) / bStone (cool). */
  private drawBelief(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const g = state.world.grid;
    const belief = state.env.belief;
    for (let r = 0; r < g; r++) {
      for (let c = 0; c < g; c++) {
        const i = r * g + c;
        if (belief.known[i] === UNKNOWN) continue;
        const bd = belief.bGold[i];
        const bb = belief.bStone[i];
        if (bd < 0.02 && bb < 0.02) continue;
        const px = ox + c * cell;
        const py = oy + r * cell;
        // Warm layer (gold) — orange.
        if (bd > 0.02) {
          ctx.fillStyle = `rgba(224, 179, 74, ${0.15 + 0.6 * bd})`;
          ctx.fillRect(px, py, cell, cell);
        }
        // Cool layer (stone) — slate blue.
        if (bb > 0.02) {
          ctx.fillStyle = `rgba(76, 114, 176, ${0.2 + 0.55 * bb})`;
          ctx.fillRect(px, py, cell, cell);
        }
      }
    }
  }

  /** Ground truth: gold (rendered as coins) and stones (rendered as rocks).
   *
   *  Painted cells are ALWAYS visible to the user — you need to see what you
   *  drew. But the agent only knows about a cell once it has sensed it, so we
   *  fade unsensed cells to signal "the agent hasn't found this yet". Cells
   *  the agent has sensed (or visited) render at full opacity; the contrast
   *  is the visual cue that the agent's knowledge is growing as it explores.
   *
   *  `revealAll` forces full opacity everywhere (the debug / spoiler toggle).
   *
   *  Fade levels: stones fade lower than gold because they fill more pixels
   *  at the same alpha; gold is a small inset coin (needs more). */
  private drawWorld(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const g = state.world.grid;
    const cells = state.world.cells;
    const known = state.env.belief.known;
    for (let r = 0; r < g; r++) {
      for (let c = 0; c < g; c++) {
        const i = r * g + c;
        const kind = cells[i];
        if (kind === FREE) continue;
        const knownHere = known[i] !== UNKNOWN;
        const fullAlpha = state.revealAll || knownHere;
        const px = ox + c * cell;
        const py = oy + r * cell;
        if (kind === GOLD) {
          ctx.globalAlpha = fullAlpha ? 1 : GOLD_FADE;
          this.drawCoin(ctx, px, py, cell, i);
        } else if (kind === STONE) {
          ctx.globalAlpha = fullAlpha ? 1 : STONE_FADE;
          this.drawRock(ctx, px, py, cell, i);
        }
        ctx.globalAlpha = 1;
      }
    }
  }

  /** Draw a coin: radial-gradient body with a darker rim and an inner ring
   *  engraved on its face. Sized ~70% of the cell, centred. */
  private drawCoin(
    ctx: CanvasRenderingContext2D,
    px: number,
    py: number,
    cell: number,
    seed: number,
  ): void {
    const cx = px + cell * 0.5;
    const cy = py + cell * 0.5;
    const radius = cell * 0.34;
    // Body — radial gradient (highlight upper-left → shadow lower-right).
    const grad = ctx.createRadialGradient(
      cx - radius * 0.35,
      cy - radius * 0.35,
      radius * 0.1,
      cx,
      cy,
      radius,
    );
    grad.addColorStop(0, GOLD_HIGHLIGHT);
    grad.addColorStop(0.55, GOLD_COLOR);
    grad.addColorStop(1, GOLD_SHADOW);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    // Rim — darker outline gives the coin edge.
    ctx.strokeStyle = GOLD_SHADOW;
    ctx.lineWidth = Math.max(0.6, cell * 0.04);
    ctx.stroke();
    // Inner ring — the engraved face detail.
    ctx.strokeStyle = GOLD_INNER;
    ctx.lineWidth = Math.max(0.4, cell * 0.03);
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.62, 0, Math.PI * 2);
    ctx.stroke();
    // Glint — small bright dot near the highlight.
    ctx.fillStyle = GOLD_HIGHLIGHT;
    ctx.beginPath();
    ctx.arc(cx - radius * 0.38, cy - radius * 0.38, radius * 0.14, 0, Math.PI * 2);
    ctx.fill();
    void seed;
  }

  /** Draw a rock: an irregular polygon derived deterministically from the
   *  cell index (so the same rock keeps its shape across frames), with a
   *  top-left highlight and a bottom-right shadow. Sized ~85% of the cell. */
  private drawRock(
    ctx: CanvasRenderingContext2D,
    px: number,
    py: number,
    cell: number,
    seed: number,
  ): void {
    // 7-vertex irregular polygon, deterministic per cell. mulberry32-style
    // hash so we don't need an RNG object — just a function of `seed`.
    let s = (seed * 2654435761) >>> 0;
    const rand = () => {
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const nVerts = 7;
    const baseR = cell * 0.42;
    const cx = px + cell * 0.5;
    const cy = py + cell * 0.5;
    ctx.beginPath();
    for (let k = 0; k < nVerts; k++) {
      // Angle + per-vertex radius jitter (±20%).
      const ang = (k / nVerts) * Math.PI * 2;
      const rj = baseR * (0.8 + rand() * 0.4);
      const vx = cx + Math.cos(ang) * rj;
      const vy = cy + Math.sin(ang) * rj;
      if (k === 0) ctx.moveTo(vx, vy);
      else ctx.lineTo(vx, vy);
    }
    ctx.closePath();
    // Body — linear gradient (light upper-left → dark lower-right).
    const grad = ctx.createLinearGradient(
      cx - baseR,
      cy - baseR,
      cx + baseR,
      cy + baseR,
    );
    grad.addColorStop(0, STONE_HIGHLIGHT);
    grad.addColorStop(0.5, STONE_COLOR);
    grad.addColorStop(1, STONE_SHADOW);
    ctx.fillStyle = grad;
    ctx.fill();
    // Rim — a slightly darker outline.
    ctx.strokeStyle = STONE_SHADOW;
    ctx.lineWidth = Math.max(0.5, cell * 0.03);
    ctx.stroke();
    // Two deterministic cracks — short dark strokes from the centre.
    ctx.strokeStyle = STONE_CRACK;
    ctx.lineWidth = Math.max(0.4, cell * 0.025);
    for (let k = 0; k < 2; k++) {
      const ang = rand() * Math.PI * 2;
      const r0 = baseR * 0.2;
      const r1 = baseR * (0.55 + rand() * 0.2);
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(ang) * r0, cy + Math.sin(ang) * r0);
      ctx.lineTo(cx + Math.cos(ang + 0.2) * r1, cy + Math.sin(ang + 0.2) * r1);
      ctx.stroke();
    }
  }

  /** Visited cells tint. */
  private drawVisited(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const g = state.world.grid;
    const visited = state.env.visitedCells;
    for (let i = 0; i < visited.length; i++) {
      if (!visited[i]) continue;
      const r = (i / g) | 0;
      const c = i - r * g;
      ctx.fillStyle = VISITED_TINT;
      ctx.fillRect(ox + c * cell, oy + r * cell, cell, cell);
    }
  }

  /** The agent disc + heading + sensor ring + bump flash. Eases between cells. */
  private drawAgent(
    state: RenderState,
    cell: number,
    ox: number,
    oy: number,
    t: number,
  ): void {
    const ctx = this.ctx;
    const a = state.agent;
    // Ease toward the target pose.
    const ease = 0.25;
    a.sr += (a.r - a.sr) * ease;
    a.sc += (a.c - a.sc) * ease;
    // Ease heading toward targetHeading (shortest angular distance).
    let dh = a.targetHeading - a.heading;
    while (dh > Math.PI) dh -= 2 * Math.PI;
    while (dh < -Math.PI) dh += 2 * Math.PI;
    a.heading += dh * 0.3;
    if (a.bumpFlash > 0) a.bumpFlash = Math.max(0, a.bumpFlash - 0.04);

    const px = ox + (a.sc + 0.5) * cell;
    const py = oy + (a.sr + 0.5) * cell;
    const radius = cell * 0.34;

    // Sensor ring — the (2·SENSOR_RADIUS + 1)² sensing radius around the
    // agent (SENSOR_RADIUS = 3 → a 7×7 window).
    ctx.strokeStyle = SENSOR_RING;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(px, py, cell * (SENSOR_RADIUS + 0.5), 0, Math.PI * 2);
    ctx.stroke();

    // Bump flash — a red ring that fades over ~400 ms.
    if (a.bumpFlash > 0) {
      ctx.strokeStyle = `rgba(196, 78, 82, ${a.bumpFlash})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(px, py, radius * (1.4 + (1 - a.bumpFlash) * 0.8), 0, Math.PI * 2);
      ctx.stroke();
    }

    // Mixture-of-Bandits: green halo when the gate fired this tick. Same
    // pattern as bump-flash, opposite colour (good = green).
    if (state.gateFiring) {
      ctx.strokeStyle = 'rgba(85, 168, 104, 0.85)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(px, py, radius * 1.6, 0, Math.PI * 2);
      ctx.stroke();
    }

    // MoB Global: blue halo when the queen meta-action fired this tick
    // (the agent jumped to an argmax-density cell). Slightly larger than
    // the gate halo; the two never co-occur (queen actions live at indices
    // the gate doesn't mask).
    if (state.queenFiring) {
      ctx.strokeStyle = 'rgba(76, 114, 176, 0.85)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(px, py, radius * 1.8, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Body.
    ctx.fillStyle = AGENT_BODY;
    ctx.strokeStyle = AGENT_RING;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Heading tick.
    ctx.strokeStyle = AGENT_RING;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(
      px + Math.cos(a.heading) * radius,
      py + Math.sin(a.heading) * radius,
    );
    ctx.stroke();
    // Suppress unused-warning in some build configs (t used for future anim).
    void t;
  }
}
